import {
    findAnswerTargetElement,
    getAnswerTargetScrollPlan,
    getAnswerTargetScrollTop,
} from 'lib/answerTargetScroll'
import { answerRowSelector } from 'lib/threadOpenPlacement'

export const THREAD_OPEN_REVEAL_DURATION_MS = 320
export const THREAD_OPEN_REVEAL_LEAD_PX = 112
// Optional context may settle briefly, but can never veto an already loaded target.
const THREAD_OPEN_CONTEXT_SETTLE_MS = 250

// Include the head (which can move the entire tail) and a bounded context window.
export function threadOpenContextIds(answerIds: string[], answerId: string): string[] {
    const index = answerIds.indexOf(answerId)
    if (index < 0) return [answerId]
    return [...new Set([answerIds[0], ...answerIds.slice(Math.max(0, index - 4), index + 6)])]
}

const renderedAnswer = (root: HTMLElement, answerId: string) => {
    const target = findAnswerTargetElement(root, answerId)
    return target?.matches('[data-testid="answer-root"]') ||
        target?.querySelector('[data-testid="answer-root"]')
        ? target
        : null
}

const scrollKeys = new Set(['ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp', ' '])
const activeAnchors = new WeakMap<HTMLElement, { release: () => void }>()

// The same owner holds the staged position, drives the animation, and compensates
// later layout changes. Its offset is relative to live target geometry, not an old
// absolute scrollTop. Resize delivery corrects before paint, without a second rAF.
export function installThreadOpenAnchor(scrollElement: HTMLElement, answerId: string) {
    const resolveTarget = () => renderedAnswer(scrollElement, answerId)
    const target = resolveTarget()
    const content = scrollElement.querySelector('.transform-container')
    if (!target || !content) return null
    activeAnchors.get(scrollElement)?.release()

    let offset = getAnswerTargetScrollTop(scrollElement, target) - scrollElement.scrollTop
    let expectedTop = scrollElement.scrollTop
    let released = false
    const previousOverflowAnchor = scrollElement.style.overflowAnchor
    const place = (nextOffset = offset) => {
        if (released) return false
        const currentTarget = resolveTarget()
        if (!currentTarget) {
            release()
            return false
        }
        offset = nextOffset
        const nextTop = Math.max(0, getAnswerTargetScrollTop(scrollElement, currentTarget) - offset)
        if (Math.abs(scrollElement.scrollTop - nextTop) > 0.25) scrollElement.scrollTop = nextTop
        expectedTop = scrollElement.scrollTop
        return true
    }
    const onScroll = () => {
        if (Math.abs(scrollElement.scrollTop - expectedTop) > 0.5) release()
    }
    const onKeyDown = (event: KeyboardEvent) => {
        if (scrollKeys.has(event.key)) release()
    }
    const release = () => {
        if (released) return
        released = true
        resizeObserver?.disconnect()
        mutationObserver?.disconnect()
        for (const event of ['wheel', 'touchstart', 'pointerdown']) {
            scrollElement.removeEventListener(event, release)
        }
        scrollElement.removeEventListener('scroll', onScroll)
        window.removeEventListener('keydown', onKeyDown)
        if (activeAnchors.get(scrollElement)?.release === release)
            activeAnchors.delete(scrollElement)
        scrollElement.style.overflowAnchor = previousOverflowAnchor
        if (scrollElement.dataset.naturalAnswerAnchorOwned === answerId) {
            delete scrollElement.dataset.naturalAnswerAnchorOwned
        }
    }
    const observeSegments = () => {
        content
            .querySelectorAll(
                '[data-natural-flow-segment-mounted="true"], [data-testid="answer-root"]',
            )
            .forEach(node => {
                resizeObserver?.observe(node)
            })
    }
    const resizeObserver =
        typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => place())
    const mutationObserver =
        typeof MutationObserver === 'undefined'
            ? null
            : new MutationObserver(() => {
                  observeSegments()
                  place()
              })
    scrollElement.style.overflowAnchor = 'none'
    scrollElement.dataset.naturalAnswerAnchorOwned = answerId
    resizeObserver?.observe(content)
    observeSegments()
    mutationObserver?.observe(content, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
            'data-natural-flow-geometry-ready',
            'data-natural-flow-segment-mounted',
            'style',
            'class',
        ],
    })
    for (const event of ['wheel', 'touchstart', 'pointerdown']) {
        scrollElement.addEventListener(event, release, { passive: true })
    }
    scrollElement.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('keydown', onKeyDown)
    activeAnchors.set(scrollElement, { release })
    return { place, release, isReleased: () => released }
}

function visibleGeometryReady(scrollElement: HTMLElement) {
    const viewport = scrollElement.getBoundingClientRect()
    const pending = scrollElement.querySelectorAll(
        '[data-natural-flow-segment-mounted="false"], ' +
            '[data-natural-flow-geometry-ready="false"], ' +
            '[data-natural-flow-geometry-pending="true"]',
    )
    return !Array.from(pending).some(element => {
        const rect = element.getBoundingClientRect()
        return rect.bottom > viewport.top && rect.top < viewport.bottom
    })
}

type RevealOptions = {
    scrollElement: HTMLElement
    answerId: string
    prepare: () => Promise<string[]>
    hidden: boolean
    onReveal: () => void
    retainAnchor: (release: () => void) => void
    shouldCancel: () => boolean
    deadlineAt: number
}

export async function revealThreadOpen({
    scrollElement,
    answerId,
    prepare,
    hidden,
    onReveal,
    retainAnchor,
    shouldCancel,
    deadlineAt,
}: RevealOptions): Promise<'done' | 'not-in-quest' | 'timeout' | 'cancelled'> {
    let contextIds: string[] | null = null
    let preparationFailed = false
    void prepare().then(
        ids => (contextIds = ids),
        () => (preparationFailed = true),
    )
    const nextFrame = () => new Promise<number>(resolve => window.requestAnimationFrame(resolve))
    let anchor: ReturnType<typeof installThreadOpenAnchor> = null
    let retained = false
    let lead = 0
    let stableFrames = 0
    let lastGeometry = ''
    let targetReadyAt: number | null = null
    // A reused visible panel must not drift while its new neighborhood hydrates.
    const viewport = scrollElement.getBoundingClientRect()
    const visibleAnswer = hidden
        ? null
        : Array.from(
              scrollElement.querySelectorAll<HTMLElement>('[data-testid="answer-root"]'),
          ).find(element => {
              const rect = element.getBoundingClientRect()
              return rect.bottom > viewport.top && rect.top < viewport.bottom
          })
    let preparationAnchor = visibleAnswer?.dataset.answerId
        ? installThreadOpenAnchor(scrollElement, visibleAnswer.dataset.answerId)
        : null
    const cancelled = () =>
        shouldCancel() || !!anchor?.isReleased() || !!preparationAnchor?.isReleased()

    try {
        // Membership and a real target are required. Surrounding skeletons only affect
        // whether we can offer the context animation, never whether the post opens.
        while (Date.now() < deadlineAt && !preparationFailed) {
            if (cancelled()) return 'cancelled'
            await nextFrame()
            if (cancelled()) return 'cancelled'
            const target = renderedAnswer(scrollElement, answerId)
            if (
                !target &&
                scrollElement.querySelector(
                    `${answerRowSelector(answerId)}[data-thread-answer-unavailable="true"]`,
                )
            ) {
                return 'not-in-quest'
            }
            if (!target || !contextIds || scrollElement.clientHeight <= 0) continue
            targetReadyAt ??= Date.now()

            if (!anchor && hidden) {
                const plan = getAnswerTargetScrollPlan({
                    currentScrollTop: scrollElement.scrollTop,
                    finalScrollTop: getAnswerTargetScrollTop(scrollElement, target),
                    maxScrollTop: scrollElement.scrollHeight - scrollElement.clientHeight,
                    leadOffsetPx: THREAD_OPEN_REVEAL_LEAD_PX,
                })
                lead = plan.finalScrollTop - plan.startScrollTop
                anchor = installThreadOpenAnchor(scrollElement, answerId)
                if (!anchor) continue
                anchor.place(lead)
            }
            // Staging while hidden lets intersection-driven rows in the actual reveal
            // window mount too. A visible reused panel keeps its current position here.
            const ready = !hidden || visibleGeometryReady(scrollElement)
            const geometry = [
                getAnswerTargetScrollTop(scrollElement, target),
                target.getBoundingClientRect().height,
                scrollElement.scrollHeight,
                scrollElement.clientWidth,
            ].join(':')
            stableFrames = ready && geometry === lastGeometry ? stableFrames + 1 : 0
            lastGeometry = geometry
            const skipUnsettledContext =
                Date.now() - targetReadyAt >= THREAD_OPEN_CONTEXT_SETTLE_MS ||
                deadlineAt - Date.now() < 50
            if (stableFrames < 2 && !skipUnsettledContext) continue

            if (!anchor) {
                preparationAnchor?.release()
                preparationAnchor = null
                const plan = getAnswerTargetScrollPlan({
                    currentScrollTop: scrollElement.scrollTop,
                    finalScrollTop: getAnswerTargetScrollTop(scrollElement, target),
                    maxScrollTop: scrollElement.scrollHeight - scrollElement.clientHeight,
                    leadOffsetPx: THREAD_OPEN_REVEAL_LEAD_PX,
                })
                lead = plan.finalScrollTop - plan.startScrollTop
                anchor = installThreadOpenAnchor(scrollElement, answerId)
                if (!anchor) continue
                anchor.place(lead)
            }
            const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
            const hasTimeForContextAnimation =
                deadlineAt - Date.now() >= THREAD_OPEN_REVEAL_DURATION_MS
            const animateContext =
                stableFrames >= 2 &&
                !reducedMotion &&
                Math.abs(lead) > 1 &&
                hasTimeForContextAnimation
            if (!animateContext) anchor.place(0)
            onReveal()
            if (!animateContext) {
                retainAnchor(anchor.release)
                retained = true
                return 'done'
            }
            const start = performance.now()
            while (true) {
                const now = await nextFrame()
                if (cancelled()) return 'cancelled'
                const progress = Math.min(1, (now - start) / THREAD_OPEN_REVEAL_DURATION_MS)
                anchor.place(lead * Math.pow(1 - progress, 3))
                if (progress >= 1) break
            }
            if (cancelled()) return 'cancelled'
            anchor.place(0)
            retainAnchor(anchor.release)
            retained = true
            return 'done'
        }
        return shouldCancel() ? 'cancelled' : 'timeout'
    } finally {
        preparationAnchor?.release()
        if (!retained) anchor?.release()
    }
}
